/**
 * Patch global de `window.fetch` pour la protection CSRF — B-12 Phase 5.
 *
 * Le frontend mixe axios (déjà couvert par `axiosConfig.js`) et `fetch()`
 * natif (~72 fichiers, ~197 appels). Plutôt que de migrer chaque appel,
 * on intercepte `window.fetch` une seule fois pour :
 *  1. Lire le cookie `csrf_token` (non HttpOnly, posé au login)
 *  2. Ajouter le header `X-CSRF-Token` sur les mutations
 *     (POST/PUT/PATCH/DELETE) si le cookie existe
 *  3. Activer `credentials: 'include'` pour que le cookie HttpOnly
 *     `access_token` soit toujours envoyé (utile en cas de proxy
 *     cross-origin)
 *
 * Le patch est idempotent : il préserve les headers existants, ne touche
 * pas au Content-Type (important pour FormData/multipart), et ne change
 * pas la méthode ou le body.
 *
 * USAGE : importer dans `App.js` AVANT toute import qui appellerait fetch.
 *
 *   import './config/fetchCsrfPatch';
 */

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return '';
  const parts = document.cookie.split(';');
  for (const c of parts) {
    const trimmed = c.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return '';
}

function headerHas(headers, name) {
  if (!headers) return false;
  const lower = name.toLowerCase();
  if (headers instanceof Headers) {
    return headers.has(name);
  }
  if (Array.isArray(headers)) {
    return headers.some(([k]) => (k || '').toLowerCase() === lower);
  }
  if (typeof headers === 'object') {
    return Object.keys(headers).some((k) => k.toLowerCase() === lower);
  }
  return false;
}

function injectHeader(headers, name, value) {
  // Cas 1 : Headers instance
  if (headers instanceof Headers) {
    const next = new Headers(headers);
    next.set(name, value);
    return next;
  }
  // Cas 2 : tableau de tuples
  if (Array.isArray(headers)) {
    return [...headers, [name, value]];
  }
  // Cas 3 : objet plain (le plus courant) ou undefined
  return { ...headers, [name]: value };
}

if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.__csrfFetchPatched) {
  const originalFetch = window.fetch.bind(window);

  window.fetch = function patchedFetch(input, init) {
    try {
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

      if (MUTATING_METHODS.has(method)) {
        const csrf = readCookie('csrf_token');
        if (csrf) {
          const nextInit = { ...init };
          // N'écrase pas un header déjà fourni explicitement par l'appelant
          if (!headerHas(nextInit.headers, 'X-CSRF-Token')) {
            nextInit.headers = injectHeader(nextInit.headers, 'X-CSRF-Token', csrf);
          }
          // S'assure que les cookies (access_token HttpOnly) sont envoyés
          if (!nextInit.credentials) {
            nextInit.credentials = 'include';
          }
          return originalFetch(input, nextInit);
        }
      }
    } catch (e) {
      // En cas d'erreur dans le patch, on fallback sur fetch natif pour ne
      // jamais casser une requête à cause du wrapper CSRF.
      // eslint-disable-next-line no-console
      console.warn('[csrf-fetch] patch error, falling back to native fetch:', e);
    }
    return originalFetch(input, init);
  };

  window.__csrfFetchPatched = true;
}

export default {};
